import { useState } from 'react';
import { MarketingProject, MarketingPage } from '../types';
import { useMarketingData } from '../MarketingContext';

interface Props { onNavigate: (page: MarketingPage) => void; }

export default function ProjectsPage({ onNavigate: _onNavigate }: Props) {
  const { companies, projects, products, campaigns } = useMarketingData();
  const [search, setSearch] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedProject, setSelectedProject] = useState<MarketingProject | null>(null);
  const [view, setView] = useState<'list' | 'kanban'>('list');

  const filtered = projects.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchCompany = filterCompany === 'all' || p.companyId === filterCompany;
    const matchStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchSearch && matchCompany && matchStatus;
  });

  const kanbanCols = ['planning', 'in_progress', 'on_hold', 'completed'];
  const kanbanLabels: Record<string, string> = { planning: 'Planning', in_progress: 'Active', on_hold: 'On Hold', completed: 'Completed' };
  const kanbanColors: Record<string, string> = { planning: 'border-amber-300 bg-amber-50', in_progress: 'border-blue-300 bg-blue-50', on_hold: 'border-gray-300 bg-gray-50', completed: 'border-green-300 bg-green-50' };

  if (selectedProject) {
    const co = companies.find(c => c.id === selectedProject.companyId);
    const projProducts = products.filter(p => selectedProject.productIds.includes(p.id));
    const projCampaigns = campaigns.filter(c => selectedProject.campaignIds.includes(c.id));
    return (
      <div className="space-y-4">
        <button onClick={() => setSelectedProject(null)} className="text-sm text-blue-600 hover:underline">← Back to Projects</button>
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{selectedProject.name}</h2>
              <p className="text-sm text-gray-500 mt-1">{selectedProject.description}</p>
              <div className="flex gap-2 mt-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${selectedProject.status === 'in_progress' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{selectedProject.status}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">{selectedProject.priority}</span>
                <span className="text-xs text-gray-400">🏢 {co?.name}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Project Details</h3>
              <div className="text-xs space-y-1.5">
                <div><span className="text-gray-500">Owner:</span> <span className="text-gray-800">{selectedProject.owner}</span></div>
                <div><span className="text-gray-500">Marketing Owner:</span> <span className="text-gray-800">{selectedProject.marketingOwner}</span></div>
                <div><span className="text-gray-500">Target:</span> <span className="text-gray-800">{selectedProject.targetAudience}</span></div>
                <div><span className="text-gray-500">Timeline:</span> <span className="text-gray-800">{selectedProject.startDate} → {selectedProject.endDate}</span></div>
                <div><span className="text-gray-500">Budget:</span> <span className="text-gray-800">₹{(selectedProject.budget / 100000).toFixed(1)}L</span></div>
                <div><span className="text-gray-500">Channels:</span> <span className="text-gray-800">{selectedProject.channels.join(', ')}</span></div>
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Milestones</h3>
              <div className="space-y-2">
                {selectedProject.milestones.map(m => (
                  <div key={m.id} className="flex items-center gap-2">
                    <div className={`h-5 w-5 rounded-full flex items-center justify-center text-xs ${m.completed ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>{m.completed ? '✓' : '○'}</div>
                    <div className="flex-1"><div className="text-xs font-medium text-gray-800">{m.name}</div></div>
                    <div className="text-[10px] text-gray-400">{m.dueDate}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">KPIs</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {selectedProject.kpis.map(kpi => (
                <div key={kpi.id} className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500">{kpi.name}</div>
                  <div className="text-lg font-bold text-gray-900">{kpi.current}<span className="text-xs text-gray-400"> / {kpi.target}</span></div>
                  <div className="text-[10px] text-gray-400">{kpi.unit}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Products</h3>
              <div className="flex flex-wrap gap-1.5">{projProducts.map(p => <span key={p.id} className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded-full">{p.name}</span>)}</div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Campaigns</h3>
              <div className="flex flex-wrap gap-1.5">{projCampaigns.map(c => <span key={c.id} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">{c.name}</span>)}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-sm text-gray-500">Manage marketing projects across all companies</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button onClick={() => setView('list')} className={`px-3 py-1 text-xs font-medium rounded-md ${view === 'list' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'}`}>List</button>
            <button onClick={() => setView('kanban')} className={`px-3 py-1 text-xs font-medium rounded-md ${view === 'kanban' ? 'bg-white text-gray-900 shadow' : 'text-gray-500'}`}>Kanban</button>
          </div>
        </div>
      </div>

      <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search projects..." className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg" />
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Companies</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Status</option>{kanbanCols.map(s => <option key={s} value={s}>{kanbanLabels[s]}</option>)}
        </select>
      </div>

      {view === 'list' ? (
        <div className="space-y-3">
          {filtered.map(p => {
            const co = companies.find(c => c.id === p.companyId);
            return (
              <button key={p.id} onClick={() => setSelectedProject(p)} className="w-full bg-white border border-gray-200 rounded-xl p-4 text-left hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-bold text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{p.description.slice(0, 100)}...</div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-gray-400">🏢 {co?.name}</span>
                      <span className="text-xs text-gray-400">💰 ₹{(p.budget / 100000).toFixed(1)}L</span>
                      <span className="text-xs text-gray-400">📅 {p.startDate} → {p.endDate}</span>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 'in_progress' ? 'bg-green-100 text-green-700' : p.status === 'planning' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
                    <div className="text-[10px] text-gray-400 mt-1">{p.milestones.filter(m => m.completed).length}/{p.milestones.length} milestones</div>
                  </div>
                </div>
                <div className="flex gap-1.5 mt-2">
                  {p.milestones.map(m => (
                    <div key={m.id} className={`h-1.5 flex-1 rounded-full ${m.completed ? 'bg-green-400' : 'bg-gray-200'}`} />
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kanbanCols.map(col => (
            <div key={col} className={`border-2 rounded-xl p-3 ${kanbanColors[col]}`}>
              <h3 className="text-xs font-bold text-gray-700 mb-3 px-1">{kanbanLabels[col]} ({filtered.filter(p => p.status === col).length})</h3>
              <div className="space-y-2">
                {filtered.filter(p => p.status === col).map(p => {
                  const co = companies.find(c => c.id === p.companyId);
                  return (
                    <button key={p.id} onClick={() => setSelectedProject(p)} className="w-full bg-white border border-gray-200 rounded-lg p-3 text-left hover:shadow-sm transition-shadow">
                      <div className="text-xs font-medium text-gray-900">{p.name}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">🏢 {co?.name}</div>
                      <div className="flex gap-1 mt-2">
                        {p.milestones.slice(0, 5).map((m, i) => (
                          <div key={i} className={`h-1 w-4 rounded-full ${m.completed ? 'bg-green-400' : 'bg-gray-200'}`} />
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
